import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import { collection, addDoc, getDocs, Timestamp, query, orderBy, where, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User } from '../types/User';
import { Event } from '../types/Event';
import { format } from 'date-fns';

interface SubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  users: User[]; // Nouvelle prop pour les utilisateurs
  selectedUserId?: string;
  selectedEventId?: string;
  editMode?: boolean;
  subscriptionToEdit?: any;
}

export const SubscriptionDialog: React.FC<SubscriptionDialogProps> = ({
  open,
  onClose,
  users, // Utilisation de la prop
  selectedUserId,
  selectedEventId,
  editMode = false,
  subscriptionToEdit = null
}) => {
  // const [users, setUsers] = useState<User[]>([]); // Supprimé
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    // Le fetchUsers interne est supprimé

    const fetchEvents = async () => {
      // Récupérer tous les événements sans filtre de date
      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        orderBy('startDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const eventsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];

      setEvents(eventsData);
    };

    if (open) {
      // fetchUsers(); // Supprimé
      fetchEvents();

      // Si on est en mode édition et qu'on a une cotisation à modifier
      if (editMode && subscriptionToEdit) {
        // Préremplir le formulaire avec les données de la cotisation
        setSelectedUser(subscriptionToEdit.userId || '');
        setSubscriptionType(subscriptionToEdit.type || '');
        setAmount(subscriptionToEdit.amount?.toString() || '');
        setPaymentMethod(subscriptionToEdit.paymentMethod || '');

        // Formater la date de paiement
        if (subscriptionToEdit.paymentDate) {
          let paymentDate;
          if (typeof subscriptionToEdit.paymentDate === 'object' && subscriptionToEdit.paymentDate.seconds) {
            // Timestamp Firestore
            paymentDate = new Date(subscriptionToEdit.paymentDate.seconds * 1000);
          } else {
            // Date string
            paymentDate = new Date(subscriptionToEdit.paymentDate);
          }

          if (!isNaN(paymentDate.getTime())) {
            setPaymentDate(paymentDate.toISOString().split('T')[0]);
          }
        }

        // Si c'est une cotisation événementielle, définir l'événement
        if (subscriptionToEdit.type === 'event' && subscriptionToEdit.eventId) {
          setSelectedEvent(subscriptionToEdit.eventId);
        }
      } else {
        resetForm();
      }
    }
  }, [open, editMode, subscriptionToEdit]);

  // Effet séparé pour gérer les valeurs présélectionnées
  useEffect(() => {
    if (open) {
      // Si un utilisateur est présélectionné, le définir
      if (selectedUserId) {
        setSelectedUser(selectedUserId);
      }
    }
  }, [open, selectedUserId]);

  // Effet pour gérer l'événement présélectionné
  useEffect(() => {
    if (open && selectedEventId) {
      // Si un événement est sélectionné, on le définit quand le type est 'event'
      if (subscriptionType === 'event') {
        setSelectedEvent(selectedEventId);
      }
    }
  }, [open, selectedEventId, subscriptionType]);

  const handleSubmit = async () => {
    try {
      // Calculer l'année de cotisation basée sur la date de paiement
      const year = new Date(paymentDate).getFullYear();

      // Préparer les données de base de la cotisation
      const subscriptionData: any = {
        userId: selectedUser,
        type: subscriptionType,
        amount: parseFloat(amount),
        paymentMethod,
        paymentDate: Timestamp.fromDate(new Date(paymentDate)),
        year: year,
      };

      // Si c'est une cotisation événementielle, ajouter l'ID de l'événement
      if (subscriptionType === 'event' && selectedEvent) {
        subscriptionData.eventId = selectedEvent;
      }

      let subscriptionId: string;

      if (editMode && subscriptionToEdit) {
        // Mode édition : mettre à jour la cotisation existante
        const subscriptionDocRef = doc(db, 'subscriptions', subscriptionToEdit.id);
        await updateDoc(subscriptionDocRef, subscriptionData);
        subscriptionId = subscriptionToEdit.id;
        console.log('Cotisation mise à jour avec ID:', subscriptionId);

        // Mettre à jour la transaction financière associée
        // D'abord trouver la transaction existante
        const financesRef = collection(db, 'finances');
        const q = query(financesRef, where('subscriptionId', '==', subscriptionId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Mettre à jour la transaction existante
          const financeDoc = querySnapshot.docs[0];
          const financeDocRef = doc(db, 'finances', financeDoc.id);

          // Préparer la description de la transaction
          let description = '';
          const user = users.find(u => u.id === selectedUser);
          const userName = user ? `${user.firstName} ${user.lastName}${user.nickname ? ` (${user.nickname})` : ''}` : '';

          if (subscriptionType === 'annual') {
            description = `Cotisation annuelle - ${userName}`;
          } else {
            const event = events.find(e => e.id === selectedEvent);
            description = `Cotisation événementielle "${event?.title}" - ${userName}`;
          }

          await updateDoc(financeDocRef, {
            amount: parseFloat(amount),
            description: description,
            date: paymentDate,
            userId: selectedUser,
            eventId: subscriptionType === 'event' ? selectedEvent : null
          });
        }
      } else {
        // Mode création : ajouter une nouvelle cotisation
        subscriptionData.createdAt = Timestamp.now();
        subscriptionData.createdBy = 'admin';

        // Ajouter la cotisation et récupérer la référence du document créé
        const subscriptionRef = await addDoc(collection(db, 'subscriptions'), subscriptionData);

        // Récupérer l'ID généré
        subscriptionId = subscriptionRef.id;
        console.log('Cotisation créée avec ID:', subscriptionId);

        // Préparer la description de la transaction
        let description = '';
        const user = users.find(u => u.id === selectedUser);
        const userName = user ? `${user.firstName} ${user.lastName}${user.nickname ? ` (${user.nickname})` : ''}` : '';

        if (subscriptionType === 'annual') {
          description = `Cotisation annuelle - ${userName}`;
        } else {
          const event = events.find(e => e.id === selectedEvent);
          description = `Cotisation événementielle "${event?.title}" - ${userName}`;
        }

        // Ajouter la transaction financière
        await addDoc(collection(db, 'finances'), {
          type: 'income',
          category: 'subscription',
          amount: parseFloat(amount),
          description: description,
          date: paymentDate,
          userId: selectedUser,
          createdAt: Timestamp.now(),
          subscriptionId: subscriptionId,
          eventId: subscriptionType === 'event' ? selectedEvent : null
        });
      }

      // Réinitialiser le formulaire et fermer
      resetForm();
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la cotisation:', error);
    }
  };

  const resetForm = () => {
    setSelectedUser('');
    setSubscriptionType('');
    setSelectedEvent('');
    setAmount('');
    setPaymentMethod('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editMode ? 'Modifier la cotisation' : 'Nouvelle cotisation'}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel id="user-label">Adhérent*</InputLabel>
          <Select
            labelId="user-label"
            value={selectedUser}
            label="Adhérent*"
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.firstName} {user.lastName} {user.nickname ? `(${user.nickname})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="type-label">Type de cotisation*</InputLabel>
          <Select
            labelId="type-label"
            value={subscriptionType}
            label="Type de cotisation*"
            onChange={(e) => {
              const newType = e.target.value;
              setSubscriptionType(newType);
              // Réinitialiser l'événement sélectionné si on change de type
              if (newType !== 'event') {
                setSelectedEvent('');
              } else if (!selectedEventId) {
                // Si on passe en "événementielle" et qu'aucun événement n'est forcé par les props,
                // on essaie de trouver un événement en cours aujourd'hui/maintenant
                const now = new Date();
                const currentEvent = events.find(event => {
                  if (!event.startDate) return false;
                  const start = new Date(event.startDate);
                  // S'il y a une date de fin, on regarde si on est entre les deux.
                  // Sinon, on regarde si ça a commencé il y a moins de 24h.
                  if (event.endDate) {
                    const end = new Date(event.endDate);
                    return now >= start && now <= end;
                  } else {
                    const diffTime = Math.abs(now.getTime() - start.getTime());
                    const diffDays = diffTime / (1000 * 60 * 60 * 24);
                    return diffDays <= 1;
                  }
                });
                if (currentEvent && currentEvent.id) {
                  setSelectedEvent(currentEvent.id);
                }
              }
            }}
          >
            <MenuItem value="annual">Annuelle</MenuItem>
            <MenuItem value="event">Événementielle</MenuItem>
          </Select>
        </FormControl>

        {subscriptionType === 'event' && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="event-label">Événement*</InputLabel>
            <Select
              labelId="event-label"
              value={selectedEvent}
              label="Événement*"
              onChange={(e) => setSelectedEvent(e.target.value)}
              disabled={!!selectedEventId} // Désactiver si un événement est présélectionné
            >
              {events.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.title} ({format(new Date(event.startDate), 'dd/MM/yyyy')})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Sélectionnez l'événement pour lequel cette cotisation est payée</FormHelperText>
          </FormControl>
        )}

        <TextField
          fullWidth
          label="Montant*"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          margin="normal"
          InputProps={{
            startAdornment: <span>€</span>,
          }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel id="payment-method-label">Mode de paiement*</InputLabel>
          <Select
            labelId="payment-method-label"
            value={paymentMethod}
            label="Mode de paiement*"
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <MenuItem value="especes">Espèces</MenuItem>
            <MenuItem value="virement">Virement</MenuItem>
            <MenuItem value="cheque">Chèque</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Date de paiement*"
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedUser || !subscriptionType || !amount || !paymentMethod || !paymentDate ||
            (subscriptionType === 'event' && !selectedEvent)}
        >
          {editMode ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
